/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IContentBlockAdditionalContentItemViewProps, IContentBlockAdditionalContentViewProps } from '@msdyn365-commerce-modules/content-block';
import { IContentBlockViewProps } from '@msdyn365-commerce-modules/content-block';

//----------------------------------------------------------
// @CUSTOM: Added imports for additional config from
// extended definition
//----------------------------------------------------------
import { Heading } from '@msdyn365-commerce-modules/utilities';
import { IImageData, Image } from '@msdyn365-commerce/core';
import { IContentBlockProps, IAppellationData } from '../definition-extensions/content-block.ext.props.autogenerated';
//----------------------------------------------------------

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: IContentBlockAdditionalContentItemViewProps) => {
                return (
                    <>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </>
                );
            })}
        </Node>
    );
};

//----------------------------------------------------------
/**
 * @CUSTOM: Render Icon Image.
 * @param image - Icon image from view extension.
 * @returns JSX Element.
 */
//----------------------------------------------------------
const renderIconImage = (image: IImageData, props: IContentBlockProps<{}>) => {
    return (
        <div className='ms-content-block__icon'>
            <Image
                {...image}
                gridSettings={props.context.request.gridSettings ?? {}}
                imageSettings={image?.imageSettings}
            />
        </div>
    );
};
//----------------------------------------------------------

//----------------------------------------------------------
/**
 * @CUSTOM: Render Appellation.
 * @param appellation - Appellation heading from view extension.
 * @returns JSX Element.
 */
//----------------------------------------------------------
 const renderAppellation = (appellation: IAppellationData) => {
    return (
        <Heading
            className='ms-content-block__appellation'
            headingTag={appellation.tag}
            text={appellation.text}
        />
    );
};
//----------------------------------------------------------

const ContentBlockView: React.FC<IContentBlockViewProps & IContentBlockProps<{}>> = (props) => {     // @CUSTOM: Added extended definition props
    const {
        config, // @CUSTOM: Get config from extended definition
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        additionalContent
    } = props;

    //------------------------------------------------------
    // @CUSTOM: Pulling in config from extended definition
    //------------------------------------------------------
    const {
        appellation,
        iconImage
    } = config;
    //------------------------------------------------------

    if (imageLink) {
        return (
            <Module {...contentBlockContainer}>
                <Node
                    {...contentBlockAnchorTag}
                    href={imageLink}
                    className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                    aria-label={imageAriaLabel}
                >
                    <Node {...imageContainer}>{image}</Node>
                </Node>
                <Node {...detailsContainer}>
                    {/* @CUSTOM: Added icon image above content */}
                    {iconImage && renderIconImage(iconImage, props)}
                    {/* @CUSTOM: Added appellation text above title */}
                    {appellation && renderAppellation(appellation)}
                    {title}
                    {text}
                    {links}
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            </Module>
        );
    }
    return (
        <Module {...contentBlockContainer}>
            <Node {...imageContainer}>{image}</Node>
            <Node {...detailsContainer}>
                {/* @CUSTOM: Added icon image above content */}
                {iconImage && renderIconImage(iconImage, props)}
                {/* @CUSTOM: Added appellation text above title */}
                {appellation && renderAppellation(appellation)}
                {title}
                {text}
                {links}
                {additionalContent && renderAdditionalContent(additionalContent)}
            </Node>
        </Module>
    );
};

export default ContentBlockView;
